import {
  Badge,
  Box,
  Button,
  DataList,
  HStack,
  Heading,
  SimpleGrid,
  Skeleton,
  Text,
  VStack,
} from "@chakra-ui/react";
import {
  AlertTriangle,
  Banknote,
  Calendar,
  CheckCircle2,
  Clock,
  Hash,
  Info,
  Landmark,
  Receipt,
  XCircle,
} from "lucide-react";
import { useState } from "react";
import { useBuildingManagementEnabled } from "../../hooks/useBuildingManagementEnabled";
import { useShowGmf } from "../../hooks/useShowGmf";
import { mapPayoutBatchType } from "../../mappers/payouts.mapper";
import type { PayoutBatch } from "../../types/payouts.type";

export default function BatchInfo({
  batch,
  isLoading,
}: {
  batch?: PayoutBatch;
  isLoading: boolean;
}) {
  const isBuildingManagementEnabled = useBuildingManagementEnabled();
  const showGmf = useShowGmf();

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("es-CO", {
      style: "currency",
      currency: "COP",
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return "";
    return new Intl.DateTimeFormat("es-CO", {
      day: "2-digit",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    }).format(new Date(dateString));
  };

  const shadow =
    "rgba(0, 0, 0, 0) 0px 0px 0px 0px, rgba(0, 0, 0, 0) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 1px 2px 0px";

  const [showError, setShowError] = useState(false);

  return (
    <Box
      p={{ base: 4, md: 8 }}
      bg="white"
      borderRadius="lg"
      boxShadow={shadow}
      mb={8}
    >
      <HStack
        justifyContent="space-between"
        alignItems="flex-start"
        mb={{ base: 6, md: 10 }}
        flexDirection={{ base: "column", md: "row" }}
        gap={4}
      >
        <VStack align="flex-start" gap={1}>
          <HStack gap={2} color="gray.400">
            <Info size={16} />
            <Text
              fontSize="xs"
              fontWeight="bold"
              textTransform="uppercase"
              letterSpacing="wider"
            >
              Detalles del Lote
            </Text>
          </HStack>
          <Heading size="xl" color="gray.900" fontWeight="extrabold">
            Información del Lote
          </Heading>
        </VStack>

        <Skeleton loading={isLoading} borderRadius="full">
          {batch && (
            <Badge
              colorPalette={
                batch.status === "approved"
                  ? "green"
                  : batch.status === "cancelled"
                  ? "red"
                  : batch.status === "failed"
                  ? "red"
                  : batch.status === "preparing"
                  ? "blue"
                  : batch.status === "unfunded"
                  ? "yellow"
                  : batch.status === "funding"
                  ? "teal"
                  : "orange"
              }
              size="lg"
              variant="subtle"
              px={4}
              py={1.5}
              borderRadius="full"
              fontSize="sm"
              fontWeight="bold"
            >
              <HStack gap={2}>
                {batch.status === "approved" ? (
                  <CheckCircle2 size={14} />
                ) : batch.status === "cancelled" ||
                  batch.status === "failed" ? (
                  <XCircle size={14} />
                ) : (
                  <Clock size={14} />
                )}
                {batch.status === "approved"
                  ? "Lote Aprobado"
                  : batch.status === "cancelled"
                  ? "Lote Cancelado"
                  : batch.status === "failed"
                  ? "Lote Fallido"
                  : batch.status === "preparing"
                  ? "Preparando Lote"
                  : batch.status === "unfunded"
                  ? "Sin fondos"
                  : batch.status === "funding"
                  ? "Fondos en camino"
                  : "Pendiente de Aprobación"}
              </HStack>
            </Badge>
          )}
        </Skeleton>
      </HStack>

      <SimpleGrid columns={{ base: 1, md: 2 }} gap={{ base: 6, md: 16 }}>
        <DataList.Root orientation="horizontal">
          <DataList.Item>
            <DataList.ItemLabel
              color="gray.500"
              display="flex"
              alignItems="center"
              gap={2}
            >
              <Hash size={14} /> ID de Referencia
            </DataList.ItemLabel>
            <DataList.ItemValue>
              <Skeleton loading={isLoading} height="24px">
                <Text fontWeight="bold" color="gray.800" fontFamily="mono">
                  {batch?.id}
                </Text>
              </Skeleton>
            </DataList.ItemValue>
          </DataList.Item>

          <DataList.Item>
            <DataList.ItemLabel
              color="gray.500"
              display="flex"
              alignItems="center"
              gap={2}
            >
              <Calendar size={14} /> Fecha de Carga
            </DataList.ItemLabel>
            <DataList.ItemValue>
              <Skeleton loading={isLoading} height="24px">
                <Text
                  fontWeight="medium"
                  color="gray.700"
                  fontSize={{ base: "sm", md: "md" }}
                >
                  {formatDate(batch?.createdAt).replace(".", "")}
                </Text>
              </Skeleton>
            </DataList.ItemValue>
          </DataList.Item>

          {isBuildingManagementEnabled && (
            <DataList.Item>
              <DataList.ItemLabel
                color="gray.500"
                display="flex"
                alignItems="center"
                gap={2}
              >
                <Landmark size={14} /> Tipo de Lote
              </DataList.ItemLabel>
              <DataList.ItemValue>
                <Skeleton loading={isLoading} height="24px" width="120px">
                  {batch && (
                    <Badge
                      colorPalette={
                        mapPayoutBatchType[batch.type ?? "owner"].color
                      }
                      variant="subtle"
                    >
                      {mapPayoutBatchType[batch.type ?? "owner"].label}
                    </Badge>
                  )}
                </Skeleton>
              </DataList.ItemValue>
            </DataList.Item>
          )}
        </DataList.Root>

        <DataList.Root orientation="horizontal">
          <DataList.Item>
            <DataList.ItemLabel
              color="gray.500"
              display="flex"
              alignItems="center"
              gap={2}
            >
              <Banknote size={14} /> Cantidad de Pagos
            </DataList.ItemLabel>
            <DataList.ItemValue>
              <Skeleton loading={isLoading} height="24px" width="100px">
                <HStack gap={1.5}>
                  <Text fontWeight="bold" color="gray.800">
                    {batch?.totalCount}
                  </Text>
                  <Text
                    color="gray.500"
                    fontWeight="medium"
                    display={{ base: "none", sm: "inline" }}
                  >
                    transacciones
                  </Text>
                </HStack>
              </Skeleton>
            </DataList.ItemValue>
          </DataList.Item>

          <DataList.Item>
            <DataList.ItemLabel
              color="gray.500"
              display="flex"
              alignItems="center"
              gap={2}
            >
              <Banknote size={14} /> Monto Total
            </DataList.ItemLabel>
            <DataList.ItemValue>
              <Skeleton loading={isLoading} height="32px" width="240px">
                <Text
                  fontSize={{ base: "xl", md: "2xl" }}
                  fontWeight="black"
                  color="palomma.500"
                  lineHeight="1"
                >
                  {batch ? formatCurrency(batch.totalAmount) : ""}
                </Text>
              </Skeleton>
            </DataList.ItemValue>
          </DataList.Item>

          {showGmf && (
            <DataList.Item>
              <DataList.ItemLabel
                color="gray.500"
                display="flex"
                alignItems="center"
                gap={2}
              >
                <Receipt size={14} /> GMF (4x1000)
              </DataList.ItemLabel>
              <DataList.ItemValue>
                <Skeleton loading={isLoading} height="24px" width="100px">
                  <Text fontWeight="medium" color="gray.700">
                    {batch ? formatCurrency(batch.gmfTotal ?? 0) : ""}
                  </Text>
                </Skeleton>
              </DataList.ItemValue>
            </DataList.Item>
          )}
        </DataList.Root>
      </SimpleGrid>

      {batch?.errors && batch.errors.length > 0 && (
        <Box mt={6}>
          <Button
            variant="ghost"
            size="sm"
            colorPalette="red"
            onClick={() => setShowError(!showError)}
            w="full"
            justifyContent="flex-start"
          >
            <AlertTriangle size={16} />
            {showError
              ? "Ocultar errores"
              : `Mostrar errores (${batch.errors.length})`}
          </Button>
          {showError && (
            <VStack align="stretch" gap={2} mt={2}>
              {batch.errors.map((error, idx) => (
                <Box
                  key={idx}
                  p={3}
                  bg="red.50"
                  borderRadius="md"
                  border="1px solid"
                  borderColor="red.200"
                >
                  <Text fontSize="xs" color="red.500" fontWeight="bold" mb={1}>
                    Fila {error.index + 1}: {error.requestLine}
                  </Text>
                  <Text fontSize="sm" color="red.700">
                    {error.errorMessage}
                  </Text>
                </Box>
              ))}
            </VStack>
          )}
        </Box>
      )}
    </Box>
  );
}
